import type { UserResource } from "@exonn/js-sdk";
import type { StorageKey } from "@exonn/storage";
import {
  getUserDataPayload,
  getUserDataTimestamp,
} from "src/user-data/getUserDataPayload.ts";
import zod, { type ZodType } from "zod";
import { logger } from "src/logger";
import { getUserDataStorageKey } from "src/user-data/cache/getUserDataStorageKey.ts";
import { memoryStorageDriver } from "src/storage/memoryStorage.driver.ts";
import { localStorageDriver } from "src/storage/localeStorage.driver.ts";

const defaultSanitazor = <V = any>(data: V) => data;

const defaultValidationSchema = zod.any();

export function getUserDataState<V = any, D extends V = V>(options: {
  user: UserResource;
  sanitaze?: (data: V) => void;
  validationSchema?: ZodType;
  key: StorageKey;
  defaultValue: D;
}): V;
export function getUserDataState<V = any, D extends V = V>(options: {
  user: UserResource;
  key: StorageKey;
  validationSchema?: ZodType;
  sanitaze?: (data: V) => V;
  defaultValue?: D;
}): V | undefined;
export function getUserDataState<V = any, D extends V = V>(options: {
  user: UserResource;
  key: StorageKey;
  validationSchema?: ZodType;
  sanitaze?: (data: V) => V;
  defaultValue?: D;
}): V | undefined {
  const {
    user,
    key,
    defaultValue,
    sanitaze = defaultSanitazor,
    validationSchema = defaultValidationSchema,
  } = options;

  const key_ = getUserDataStorageKey({
    user,
    key,
  });
  const memoryValue = memoryStorageDriver.get<V>(key_);

  if (memoryValue != null) {
    return memoryValue;
  }

  const devicePersistedValue = localStorageDriver.getFull<V>(key_);

  const databaseValue = getUserDataPayload<V>(user, key);

  const databaseValueTimestamp = getUserDataTimestamp(user, key);

  let persistedValue = databaseValue;

  if (
    devicePersistedValue != null &&
    databaseValueTimestamp &&
    devicePersistedValue.timestamp > +databaseValueTimestamp
  ) {
    persistedValue = devicePersistedValue.state;
  }

  if (persistedValue) {
    const result = validationSchema.safeParse(persistedValue);
    if (!result.success) {
      logger.error(result.error, {
        key,
        userId: user.id,
      });
      return defaultValue;
    }

    try {
      return sanitaze(persistedValue);
    } catch (error: any) {
      logger.error(error, {
        key,
        userId: user.id,
      });
      return defaultValue;
    }
  }

  return defaultValue;
}
